import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Plus, Clock, Users, Trash2, Edit2, Settings } from 'lucide-react';
import { GlassButton } from '../components/ui/GlassButton';
import { CreatePlanModal } from '../components/CreatePlanModal';
import { Toast } from '../Toast'; // Ensure this path is correct
import GatewaySettingsModal from '../components/GatewaySettingsModal';

export const Plans = () => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [planToEdit, setPlanToEdit] = useState<any | null>(null);
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
    const [showGatewaySettings, setShowGatewaySettings] = useState(false);
    const { isAdmin, isFreePlan } = useAuth();

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await api.get('/plans');
            setPlans(res.data);
        } catch (e) {
            console.error(e);
            setToast({ msg: "Erro ao carregar planos.", type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    const handleDelete = async (id: string) => {
        if (!window.confirm("Tem certeza que deseja excluir este plano?")) return;
        try {
            await api.delete(`/plans/${id}`);
            setToast({ msg: "Plano excluído com sucesso!", type: 'success' });
            fetchPlans();
        } catch (e) {
            console.error(e);
            setToast({ msg: "Erro ao excluir plano.", type: 'error' });
        }
    };

    return (
        <div className="dashboard-view">
            <style>{`
                .plan-card:hover {
                    transform: translateY(-5px);
                    border-color: #ff6a00 !important;
                    box-shadow: none !important;
                }
                .plan-card:hover .card-actions {
                    opacity: 1 !important;
                }
            `}</style>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="top-bar">
                <h1 className="page-title">Planos de Assinatura</h1>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {isAdmin && (
                        <GlassButton
                            onClick={() => setShowGatewaySettings(true)}
                        >
                            <Settings size={18} strokeWidth={2.5} /> Configurações Gateway
                        </GlassButton>
                    )}
                    {!isFreePlan && (
                        <GlassButton
                            onClick={() => setShowCreateModal(true)}
                        >
                            <Plus size={18} /> Novo Plano
                        </GlassButton>
                    )}
                </div>
            </div>

            {loading && plans.length === 0 ? (
                null
            ) : plans.length === 0 ? (
                <div className="empty-state-pro">
                    <div className="empty-icon-box">
                        <CreditCard size={48} />
                    </div>
                    <h3>Monetize seus Canais</h3>
                    <p>Crie planos de assinatura, configure preços e integre com gateways de pagamento.</p>
                </div>
            ) : (
                <div className="plans-grid">
                    {plans.map(plan => (
                        <div key={plan.id} className="plan-card" style={{
                            background: '#161618',
                            borderRadius: '18px',
                            border: '1px solid #333',
                            padding: '24px',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                        }}>
                            {/* Top Badge for Test Mode */}
                            {plan.test_gateway && (
                                <div style={{
                                    position: 'absolute',
                                    top: '0',
                                    right: '24px',
                                    transform: 'translateY(-50%)',
                                    background: '#fbbf24',
                                    color: '#000',
                                    fontSize: '10px',
                                    fontWeight: 800,
                                    padding: '4px 12px',
                                    borderRadius: '10px',
                                    textTransform: 'uppercase',
                                    boxShadow: 'none'
                                }}>
                                    Ambiente de Teste
                                </div>
                            )}

                            {/* Header Section */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{
                                        fontSize: '20px',
                                        fontWeight: 700,
                                        margin: 0,
                                        color: '#fff',
                                        letterSpacing: '-0.5px'
                                    }}>{plan.name}</h3>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#888',
                                        marginTop: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <Users size={14} strokeWidth={2.5} />
                                        <span>{plan.group?.title || 'Grupo Desconhecido'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Description Section */}
                            <div style={{
                                flex: 1,
                                color: '#aaa',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                minHeight: '40px'
                            }}>
                                {plan.description || "Sem descrição disponível para este plano."}
                            </div>

                            {/* Divider */}
                            <div style={{ height: '1px', background: 'linear-gradient(90deg, #333 0%, transparent 100%)' }} />

                            {/* Footer Section */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}>
                                        <Clock size={14} />
                                        <span>Duração</span>
                                    </div>
                                    <strong style={{ color: '#fff', fontSize: '14px' }}>
                                        {plan.duration_type === 'lifetime' ? 'Vitalício' :
                                            `${plan.duration_value} ${plan.duration_type === 'days' ? 'Dias' : 'Meses'}`}
                                    </strong>
                                </div>

                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        fontSize: '22px',
                                        fontWeight: 800,
                                        color: '#ff6a00',
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: '2px'
                                    }}>
                                        <span style={{ fontSize: '12px', fontWeight: 600, opacity: 0.8 }}>R$</span>
                                        {parseFloat(plan.price).toFixed(2).replace('.', ',')}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons Overlay — oculto para plano free (apenas visualização) */}
                            {!isFreePlan && (
                                <div style={{
                                    display: 'flex',
                                    gap: '4px',
                                    position: 'absolute',
                                    top: '24px',
                                    right: '24px',
                                    opacity: 0.5,
                                    transition: 'opacity 0.2s'
                                }} className="card-actions">
                                    <button
                                        onClick={() => {
                                            setPlanToEdit(plan);
                                            setShowCreateModal(true);
                                        }}
                                        style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: '4px' }}
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(plan.id)}
                                        style={{ background: 'transparent', border: 'none', color: '#88a', cursor: 'pointer', padding: '4px' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showCreateModal && (
                <CreatePlanModal
                    onClose={() => {
                        setShowCreateModal(false);
                        setPlanToEdit(null);
                    }}
                    onSuccess={() => {
                        fetchPlans();
                    }}
                    planToEdit={planToEdit}
                />
            )}

            {/* Gateway Settings Modal */}
            <GatewaySettingsModal
                isOpen={showGatewaySettings}
                onClose={() => setShowGatewaySettings(false)}
            />
        </div>
    );
};
